import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Power, PowerOff, Loader2, AlertTriangle } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/components/ui/use-toast';
import { BatchProgressSheet } from '@/components/batch-operations/BatchProgressSheet';
import { useEnableAccount, useDisableAccount, partnerAccountKeys } from '@/api/partner-accounts';
import {
  useBatchOperation,
  useBatchOperations,
  type BatchOperation,
  type BatchOperationStatus,
} from '@/api/batch-operations';
import { syncedLocationKeys } from '@/api/synced-locations';

interface EnableDisableToggleProps {
  accountId: string;
  accountName: string;
  enabled: boolean;
}

const FINISHED_STATUSES: readonly BatchOperationStatus[] = ['completed', 'failed', 'cancelled'];

function isFinished(status: string | undefined): boolean {
  return status !== undefined && (FINISHED_STATUSES as readonly string[]).includes(status);
}

const IN_FLIGHT_STATUSES: readonly string[] = ['pending', 'running', 'paused'];

/** An ended disable older than this is not announced on a later visit. */
export const ANNOUNCE_ENDED_WITHIN_MS = 7 * 24 * 60 * 60 * 1000;

const SEEN_KEY_PREFIX = 'bridge:account-disable-outcome-seen:';

/** Whether this browser already showed the outcome of the operation. Storage may be unavailable. */
function outcomeSeen(operationId: string): boolean {
  try {
    return window.localStorage.getItem(SEEN_KEY_PREFIX + operationId) !== null;
  } catch {
    return false;
  }
}

function markOutcomeSeen(operationId: string): void {
  try {
    window.localStorage.setItem(SEEN_KEY_PREFIX + operationId, '1');
  } catch {
    // Private mode or full storage: the outcome may be shown again next visit.
  }
}

/**
 * Whether the account's most recent account_disable should be followed by
 * this toggle on mount: one still in flight (resume showing its progress),
 * or one that ended recently and whose outcome this browser has not shown.
 */
export function shouldAdoptDisable(
  operation: Pick<BatchOperation, 'id' | 'status' | 'completedAt'> | undefined,
  now: number = Date.now()
): boolean {
  if (operation === undefined) return false;
  if (IN_FLIGHT_STATUSES.includes(operation.status)) return true;
  if (!isFinished(operation.status) || outcomeSeen(operation.id)) return false;
  const endedAt = operation.completedAt === null ? Number.NaN : Date.parse(operation.completedAt);
  return Number.isFinite(endedAt) && now - endedAt <= ANNOUNCE_ENDED_WITHIN_MS;
}

/** The toast for each way an account_disable can end. */
export function disableFinishedToast(
  accountName: string,
  operation: Pick<BatchOperation, 'status' | 'failedCount' | 'config'>
): { title: string; description: string; variant?: 'destructive' } {
  if (operation.status === 'completed') {
    return { title: 'Account disabled', description: `${accountName} is disabled and none of its locations is published.` };
  }
  if (operation.status === 'cancelled') {
    return {
      title: 'Disable cancelled; account still enabled',
      description: `${accountName} is still enabled. Locations already unpublished stay unpublished.`,
    };
  }
  const storedError = operation.config?.errorMessage;
  const errorMessage = typeof storedError === 'string' ? storedError : null;
  return {
    title:
      operation.failedCount > 0
        ? `Account still enabled: ${String(operation.failedCount)} location(s) failed to unpublish, see the Audit Log`
        : 'Account still enabled: the disable did not finish, see the Audit Log',
    description: errorMessage ?? `${accountName} is still enabled. Run Disable again to retry what is still published.`,
    variant: 'destructive',
  };
}

export function EnableDisableToggle({
  accountId,
  accountName,
  enabled,
}: EnableDisableToggleProps): JSX.Element {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  // The account_disable batch operation followed here until it finishes: one
  // started from this toggle, or — after a reload or from another page — the
  // account's latest one, adopted on mount.
  const [disableOperationId, setDisableOperationId] = useState<string | null>(null);
  const [progressOpen, setProgressOpen] = useState(false);
  const refetchedFor = useRef<string | null>(null);

  const enableAccount = useEnableAccount();
  const disableAccount = useDisableAccount();
  // Polls every 3 s while the operation runs (useBatchOperation's refetchInterval).
  const { data: disableOperation } = useBatchOperation(disableOperationId ?? '', {
    enabled: disableOperationId !== null,
  });

  // The account's most recent account_disable (the list is newest first).
  const { data: latestDisables } = useBatchOperations({
    partnerAccountId: accountId,
    operationType: 'account_disable',
    limit: 1,
  });
  const latestDisable = latestDisables?.data[0];
  const adoptedFrom = useRef<string | null>(null);
  useEffect(() => {
    if (disableOperationId !== null || latestDisable === undefined) return;
    if (adoptedFrom.current === latestDisable.id) return;
    adoptedFrom.current = latestDisable.id;
    if (shouldAdoptDisable(latestDisable)) {
      setDisableOperationId(latestDisable.id);
    }
  }, [disableOperationId, latestDisable]);

  const disableStatus = disableOperation?.status;
  const disableRunning = disableOperationId !== null && !isFinished(disableStatus);

  // When the disable finishes, the account flag and every location's
  // publication status have changed (or not, if some unpublishes failed), and
  // the admin is told which of the three ends it reached. "completed" is
  // written only together with the account flip, so it means disabled.
  useEffect(() => {
    if (disableOperationId === null || disableOperation === undefined || !isFinished(disableStatus)) return;
    if (refetchedFor.current === disableOperationId) return;
    refetchedFor.current = disableOperationId;
    void queryClient.invalidateQueries({ queryKey: partnerAccountKeys.all });
    void queryClient.invalidateQueries({ queryKey: syncedLocationKeys.all });
    // Once per operation per browser, however many pages or reloads see it end.
    if (!outcomeSeen(disableOperationId)) {
      markOutcomeSeen(disableOperationId);
      toast(disableFinishedToast(accountName, disableOperation));
    }
  }, [disableOperationId, disableOperation, disableStatus, queryClient, toast, accountName]);

  const isLoading = enableAccount.isPending || disableAccount.isPending;

  const handleEnable = async () => {
    try {
      await enableAccount.mutateAsync(accountId);

      toast({
        title: 'Account Enabled',
        description: `${accountName} is now enabled. You can resume syncing and publishing.`,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to enable account';
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    }
  };

  const handleDisable = async () => {
    try {
      const result = await disableAccount.mutateAsync(accountId);
      setConfirmDialogOpen(false);

      if (result.status === 'queued') {
        setDisableOperationId(result.operationId);
        setProgressOpen(true);
        toast({
          title: 'Disable started',
          description:
            `Unpublishing ${String(result.totalLocations)} location(s) of ${accountName}. ` +
            'The account is disabled once every location is unpublished.',
        });
        return;
      }

      // Its (already completed) operation is announced here, not again on the next visit.
      if (result.operationId !== null) markOutcomeSeen(result.operationId);
      toast({
        title: 'Account Disabled',
        description: `${accountName} had no published locations and has been disabled.`,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to disable account';
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    }
  };

  const progressSheet = disableOperationId !== null && (
    <BatchProgressSheet operationId={disableOperationId} open={progressOpen} onOpenChange={setProgressOpen} />
  );

  if (enabled) {
    return (
      <>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            if (disableRunning) {
              setProgressOpen(true);
            } else {
              setConfirmDialogOpen(true);
            }
          }}
          disabled={isLoading}
          className="text-amber-600 hover:text-amber-700"
          data-testid="disable-account-btn"
        >
          {isLoading || disableRunning ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <PowerOff className="mr-2 h-4 w-4" />
          )}
          {disableRunning ? 'Disabling…' : 'Disable'}
        </Button>

        <Dialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
          <DialogContent data-testid="disable-account-dialog">
            <DialogHeader>
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-500" />
                <DialogTitle>Disable Account</DialogTitle>
              </div>
              <DialogDescription>
                Are you sure you want to disable &quot;{accountName}&quot;?
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-3 rounded-lg border bg-muted/30 p-4" data-testid="disable-account-explainer">
              <p className="text-sm">This starts a background operation that:</p>
              <ul className="list-inside list-disc space-y-1 text-sm text-muted-foreground">
                <li>Unpublishes every published location from ScoutAround, one at a time</li>
                <li>Blocks new bookings for those locations, keeping existing confirmed bookings valid</li>
                <li>Then disables the account and pauses any active synchronization</li>
              </ul>
              <p className="text-sm text-muted-foreground">
                You can follow its progress, and pause or cancel it. If any location cannot be unpublished, the
                account stays enabled and you can run Disable again to retry the rest.
              </p>
              <p className="text-sm font-medium">
                You can re-enable the account at any time, but locations will need to be republished manually.
              </p>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => { setConfirmDialogOpen(false); }}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleDisable}
                disabled={isLoading}
                data-testid="confirm-disable-btn"
              >
                {isLoading ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : null}
                Disable Account
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {progressSheet}
      </>
    );
  }

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={handleEnable}
        disabled={isLoading}
        className="text-green-600 hover:text-green-700"
        data-testid="enable-account-btn"
      >
        {isLoading ? (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        ) : (
          <Power className="mr-2 h-4 w-4" />
        )}
        Enable
      </Button>
      {progressSheet}
    </>
  );
}
